// src/App.js
import React, { useEffect, useState, useCallback } from "react";
import { BrowserRouter, Route, Switch, useHistory, useLocation } from "react-router-dom";
import "../src/css/global.css";

import MainSearchPage from "./page/search/MainSearchPage";
import CompanySearchPage from "./page/search/CompanySearchPage";
import { authApi } from "./page/auth/api/authApi";
import { clearPermissionCache } from "./util/useMyPermissions";
import { confirm, dismissConfirm } from "./util/confirmDialog";
import { extendSession, getSessionExpiresAt } from "./util/session";
import LoginPage from "./page/auth/user/LoginPage";
import ProposalMain from "./page/onandme_info_proposal/proposal/ProposalMain";
import ProposalUpload from "./page/onandme_info_proposal/proposal/ProposalUpload";
import { SpaceContentUploadPage } from "./page/onandme_info_proposal/key_visual/3d/SpaceContentUploader";
import { SpaceContentListPage } from "./page/onandme_info_proposal/key_visual/3d/SpaceContentListPage";
import KeyVisualUploader from "./page/onandme_info_proposal/key_visual/2d/KeyVisualUploader";
import KeyVisualListPage from "./page/onandme_info_proposal/key_visual/2d/KeyVisualListPage";
import EventVenuePage from "./page/onandme_info_proposal/event_venue/EventVenuePage";
import LoginModal from "./page/auth/user/LoginModal";
import EventVenueUploadPage from "./page/onandme_info_proposal/event_venue/EventVenueUploadPage";
import QrEventAdminListPage from "./page/qr_events/admin/QrEventAdminListPage";
import QrEventParticipantRequestForm from "./page/qr_events/user/QrEventParticipantRequestForm";
import QrEventMyParticipantCheckPage from "./page/qr_events/user/QrEventMyParticipantCheckPage";
import QrEventParticipantMyView from "./page/qr_events/user/QrEventParticipantMyView";
import AttendancePage from "./page/qr_events/admin/AttendancePage";
import PhoneSetupPage from "./page/push/PhoneSetupPage";
import BzCardLanding from "./page/qr_events/biz_card/BizCardLanding";

import ExternalKeyVisualPage from "./page/onandme_info_proposal/key_visual/2d/external/ExternalKeyVisualPage";

// ✅ ADDED: Sidebar import
import OnandmeSidebar, { OPEN_W, CLOSED_W } from "./component/OnandmeSidebar";
import ChatbotProvider from "./component/chatbot/ChatbotProvider";
import ChatbotFAB from "./component/chatbot/ChatbotFAB";
import ChatbotWindow from "./component/chatbot/ChatbotWindow";
import ChatbotShowToggle from "./component/chatbot/ChatbotShowToggle";
import ChannelPage from "./page/channel/ChannelPage";
import ArchivedChannelsPage from "./page/channel/ArchivedChannelsPage";
import BoardPage from "./page/board/BoardPage";
import ApprovalPage from "./page/approval/ApprovalPage";
import CalendarPage from "./page/calendar/CalendarPage";
import EquipmentPage from "./page/equipment/EquipmentPage";
import MeetingRoomPage from "./page/meeting-room/MeetingRoomPage";
import MeetingMinutesPage from "./page/meeting-minutes/MeetingMinutesPage";
import VehicleLogPage from "./page/vehicle/VehicleLogPage";
import FinancePage from "./page/finance/FinancePage";
import HrPage from "./page/hr/HrPage";
import MailPage      from "./page/mail/MailPage";
import ContactsPage  from "./page/contacts/ContactsPage";
import DashboardPage from "./page/dashboard/DashboardPage";
import FeedbackPage  from "./page/feedback/FeedbackPage";
import WorkRequestPage from "./page/work-request/WorkRequestPage";
import SystemSettingPage from "./page/admin/SystemSettingPage";
import LoginAnnouncementModal from "./component/common/LoginAnnouncementModal";

/**
 * 라우터 안에서 현재 경로를 읽어 <b>App 에게 되돌려주는</b> 동기화 전용 컴포넌트 (렌더 결과 없음).
 *
 * <p>[왜 필요한가 — 스크롤이 통째로 죽던 버그] App 은 {@code <BrowserRouter>} 를 자기 JSX 안에서
 * 렌더하므로 <b>라우터보다 위</b>에 있다. 즉 라우터의 location 을 구독하지 않아, 사이드바 클릭 같은
 * SPA 이동(history.push)으로는 App 이 리렌더되지 않는다. 그런데 App 은 경로를
 * {@code window.location.pathname} 에서 <b>렌더 순간에 직접</b> 읽어 레이아웃을 정하기 때문에,
 * 한번 정해진 값이 그대로 얼어붙는다.
 *
 * <p>가장 아팠던 증상: {@code /mail}·{@code /channel} 은 full-bleed 라 아래 {@code <main>} 이
 * {@code height:100vh + overflowY:hidden} 이 된다. 메일에서 사이드바로 재무·인사·캘린더로 이동하면
 * App 이 리렌더되지 않아 <b>overflowY:hidden 이 그대로 유지</b>되고, 이 페이지들은 자체 스크롤
 * 컨테이너 없이 main 스크롤에 의존하므로 휠·트랙패드·스크롤바가 전부 죽었다. 표 본문·페이지네이터·
 * 하단 저장바에 접근할 수 없어 작업 자체가 막혔다. 사이드바를 토글하면(=App 리렌더) 되살아나기
 * 때문에 재현이 들쭉날쭉해 보였고, 전체 새로고침으로는 절대 재현되지 않았다(F5 는 App 을 새 경로로
 * 다시 렌더하므로). {@code /approval} 처럼 자체 overflowY:auto 를 가진 페이지만 무사했다.
 *
 * <p>[해결] 라우터 <b>안쪽</b>에 이 컴포넌트를 두고 useLocation 으로 경로 변화를 받아 App state 로
 * 올린다. 이제 SPA 이동마다 App 이 리렌더돼 isFullBleedPage·isPublicPage 등 경로 파생값이 모두
 * 최신이 된다. (라우팅 구조를 바꾸지 않는 최소 변경 — App 을 Router 밑으로 내리는 대공사 대신.)
 */
function LocationSync({ onChange }) {
    const { pathname } = useLocation();
    useEffect(() => { onChange(pathname); }, [pathname, onChange]);
    return null;
}

/**
 * 화면을 옮기면 스크롤을 맨 위로 되돌린다 (렌더 결과 없음).
 *
 * <p>[왜 필요한가] 이 앱은 문서(html/body)가 아니라 {@code <main>} 엘리먼트가 스크롤을 담당한다
 * (height:100vh + overflowY:auto). 그래서 "이동하면 문서가 최상단으로 간다"는 <b>브라우저 기본 동작이
 * 적용되지 않는다</b> — 문서는 애초에 스크롤하지 않기 때문이다. 그런데 코드베이스에는 스크롤을
 * 되돌리는 곳이 한 곳도 없었다.
 *
 * <p>증상: 현금 흐름표에서 표를 한참 내린 뒤(scrollTop ≈ 4,500px) 지출 예정 보고서로 옮기면, 그 탭도
 * 내용이 길어 scrollTop 이 그대로 유지된 채 <b>표 중간부터</b> 그려진다. KPI 카드·상태 칩·검색창이
 * 화면 밖이라 "화면이 안 뜬다 / 스크롤이 이상하다"로 보인다. 짧은 화면으로 가면 scrollTop 이 자동으로
 * clamp 돼 멀쩡해 보이므로 증상이 들쭉날쭉했다.
 *
 * <p>[뒤로가기는 예외] history action 이 POP(뒤로/앞으로)이면 되돌리지 않는다. 사용자가 보던 자리로
 * 돌아가려는 의도이므로 위치를 건드리지 않는 편이 관례에 맞다.
 *
 * <p>재무·인사 페이지는 탭 전환도 URL 쿼리(?tab=)로 하므로 pathname 뿐 아니라 tab 도 감시한다.
 */
function ScrollToTop() {
    const { pathname, search } = useLocation();
    const history = useHistory();
    const tab = new URLSearchParams(search).get("tab") || "";
    useEffect(() => {
        if (history.action === "POP") return;
        const main = document.querySelector("main");
        if (main) main.scrollTop = 0;
    }, [pathname, tab, history.action]);
    return null;
}

/** 만료 몇 분 전에 연장을 물어볼지. 안내 문구가 이 값에서 나오므로 여기만 고치면 된다. */
const SESSION_WARN_BEFORE_MIN = 2;
const SESSION_WARN_BEFORE_MS = SESSION_WARN_BEFORE_MIN * 60 * 1000;

export default function App() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [checkingToken, setCheckingToken] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [forceLogin, setForceLogin] = useState(false);
    // 로그인 직후 1회만 뜨는 시스템 안내 공지. onLoginSuccess(=실제 로그인)에서만 true 로 켜므로
    // 새로고침/재방문(토큰 재검증)으로는 다시 뜨지 않는다 — "로그인할 때마다, 로그인당 1회".
    const [showAnnouncement, setShowAnnouncement] = useState(false);

    // ✅ ADDED: Sidebar open state (Header처럼 Sidebar에도 데이터 주입하기 위해)
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const toggleSidebar = useCallback(() => setSidebarOpen((p) => !p), []);

    const hostname = window.location.hostname.toLowerCase();
    // 경로는 state 로 들고 LocationSync 가 라우터 안에서 갱신해 준다.
    // (window.location.pathname 을 렌더마다 직접 읽으면 SPA 이동 때 값이 얼어붙는다 — LocationSync 주석 참고)
    const [pathname, setPathname] = useState(() => window.location.pathname);

    const isOnandmeHost =
        hostname === "onandme.info" || hostname === "localhost" || hostname.endsWith(".onandme.info") ||     hostname === "54.116.161.43";

    const PUBLIC_PATH_PREFIXES = [
        "/qr-event-participant-request",
        "/qr-event-participant-check",
        "/qr-event-participant-my-view",
        "/qr-event-participant-attendance",
        "/public-key-visual",
        "/external-keyvisual",
        // 휴대폰 알림 등록 — PC 에서 만든 QR 을 폰이 찍고 들어온다.
        // 로그인하지 않은 상태이므로 공개 페이지여야 하고, 사이드바도 뜨면 안 된다
        // (폰 화면에서 사이드바가 화면을 덮는다).
        "/m/setup",
    ];

    const NOT_USE_HEADER_PATH_PATH = [
        "/qr-event-participant-attendance",
        "/public-key-visual",
        "/m/setup",
    ];

    const isPublicPage =
        !isOnandmeHost ||
        PUBLIC_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix)) ||
        pathname.includes("pdf");

    const isNotUseHeaderPage = NOT_USE_HEADER_PATH_PATH.some((prefix) => pathname.startsWith(prefix));

    // 채널·메일은 앱형(전체 높이) 레이아웃 — 중앙정렬·최대폭·좌우패딩·상단패딩 없이
    // 메인 사이드바에 딱 붙는 full-bleed 로 렌더링한다 (문서형 페이지는 기존대로).
    // /board 도 좌측 게시판 사이드탭을 가진 full-bleed 화면이라 /channel 과 같은 취급이 필요하다
    // (여백을 주면 사이드탭이 메인 사이드바에서 떨어져 두 겹 패널처럼 보인다).
    const isFullBleedPage = pathname.startsWith("/channel") || pathname.startsWith("/mail")
        || pathname.startsWith("/contacts") || pathname.startsWith("/board");

    useEffect(() => {
        const onExpired = () => {
            localStorage.removeItem("auth_token");
            // 만료 시각도 같이 지운다 — 남겨 두면 아래 감시 루프가 이미 끝난 세션을 계속 만료 처리한다.
            localStorage.removeItem("auth_token_exp");
            // 다음 사람이 같은 브라우저로 로그인할 때 앞사람 권한이 남아 있으면 안 된다.
            clearPermissionCache();
            setIsAuthenticated(false);
            setForceLogin(true);
        };
        window.addEventListener("auth:expired", onExpired);
        return () => window.removeEventListener("auth:expired", onExpired);
    }, []);

    /* ── 세션 만료 감시 + 임박 안내 ────────────────────────────────────────
     * 사이드바의 남은 시간은 <b>보여주기만</b> 했다. 0 이 돼도 아무 일이 없어서, 사용자는 만료된 화면을
     * 그대로 붙들고 작업하다가 다음 요청의 401 로 튕겼다 — 그때까지 입력한 게 통째로 날아간다.
     * 그래서 만료 1분 전에 먼저 물어보고(연장할 기회), 그래도 0 이 되면 스스로 로그아웃한다.
     *
     * 검사 주기는 5초면 충분하다(세션은 분 단위다). 대신 <b>탭이 다시 보일 때 즉시</b> 한 번 더 본다 —
     * 노트북 덮개를 닫아 두면 타이머가 멈춰 있다가 깨어난 뒤에야 도는데, 그 사이 세션은 이미 끝나 있다.
     * 감시는 App 에 둔다. 사이드바에 두면 사이드바가 없는 화면에서 만료가 감지되지 않는다.
     * ------------------------------------------------------------------ */
    useEffect(() => {
        // "이 만료 시각에 대해 이미 물어봤다" 표시. 연장하면 만료 시각이 새로 바뀌므로 다시 물어보게 된다.
        // 단순 boolean 으로 두면 한 번 연장한 뒤 두 번째 만료 때 안내가 안 뜬다.
        let warnedFor = 0;

        const check = () => {
            const exp = getSessionExpiresAt();
            // 토큰이 없으면(로그인 전) 볼 것도 없다. exp 가 없을 때도 마찬가지 — 만료 판정 근거가 없다.
            if (!exp || !localStorage.getItem("auth_token")) return;

            if (Date.now() >= exp) {
                // 안내를 띄워 둔 채 만료됐다면 그 모달부터 치운다 — 답할 대상이 이미 없다.
                dismissConfirm();
                window.dispatchEvent(new CustomEvent("auth:expired"));
                return;
            }

            if (exp - Date.now() > SESSION_WARN_BEFORE_MS || warnedFor === exp) return;
            warnedFor = exp;
            (async () => {
                const ok = await confirm({
                    title: "로그인 시간이 곧 만료됩니다",
                    message: `${SESSION_WARN_BEFORE_MIN}분 뒤 자동으로 로그아웃됩니다.\n계속 사용하시려면 시간을 연장해 주세요.`,
                    confirmLabel: "연장하기",
                    cancelLabel: "그대로 두기",
                });
                // 사용자가 답하는 동안 이미 만료됐을 수 있다 — 그땐 연장할 세션이 없다.
                if (ok && getSessionExpiresAt() > Date.now()) await extendSession();
            })();
        };

        check();
        const id = setInterval(check, 5000);
        const onVisible = () => { if (document.visibilityState === "visible") check(); };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            clearInterval(id);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, []);

    const verifyToken = async () => {
        const token = localStorage.getItem("auth_token");

        if (!token) {
            setCheckingToken(false);
            setIsAuthenticated(false);
            return;
        }

        try {
            // 토큰은 인자로 넘기지 않는다 — http.js 인터셉터가 X-TOKEN 헤더로 붙이고 서버는 그것만 읽는다.
            const res = await authApi.verify();
            setIsAuthenticated(res.valid);
        } catch {
            setIsAuthenticated(false);
        } finally {
            setCheckingToken(false);
        }

        try {
            const res = await authApi.verifyAdmin();
            setIsAdmin(res.valid);
        } catch {
            setIsAdmin(false);
        }
    };

    const handleLogout = async () => {
        // authApi.logout 이 네트워크 오류로 던지면 그 다음 줄들이 실행 안 돼 토큰 잔존하던 버그.
        // 서버 콜은 best-effort, 그 이후의 클라이언트 정리는 무조건 수행.
        try { await authApi.logout(); } catch (_) { /* 무시 — 어차피 로컬 상태 비우는 게 핵심 */ }
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_token_exp");
        clearPermissionCache();
        setIsAuthenticated(false);
        setIsAdmin(false);
        setForceLogin(true);
        // 어떤 페이지에 있었든 루트(/) 로 이동. 인증 안 된 상태라 LoginModal 이 뜨고,
        // 다시 로그인하면 그대로 대시보드(/) 가 보인다. /login 으로 보내면 사이드바 + 별도 LoginPage
        // 가 동시에 보여서 어색했음.
        window.location.replace("/");
    };

    useEffect(() => {
        if (!isOnandmeHost) {
            setCheckingToken(false);
            return;
        }

        if (!isPublicPage) verifyToken();
        else setCheckingToken(false);
        // eslint-disable-next-line
    }, []);

    if (checkingToken) {
        return (
            // 이 프로젝트에는 Tailwind 가 없다 — 예전엔 여기에
            // className="flex items-center justify-center min-h-screen ..." 이 걸려 있었는데
            // 그 클래스들이 정의된 적이 없어 전부 무시됐고, 매 접속마다 이 문구가
            // 화면 왼쪽 위에 맨 텍스트로 잠깐 튀었다. 스타일은 인라인으로 직접 준다.
            <div style={{
                minHeight: "100vh",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#64748b", fontSize: 18,
            }}>
                로그인 상태 확인 중...
            </div>
        );
    }

    if ((!isAuthenticated || forceLogin) && !isPublicPage) {
        return (
            <LoginModal
                onLoginSuccess={() => {
                    setForceLogin(false);
                    setShowAnnouncement(true);   // 로그인 성공 시에만 공지 1회 노출
                    verifyToken();
                }}
            />
        );
    }

    // 챗봇은 로그인된 onandme.info 환경에서만 노출
    const chatbotAuthed = isAuthenticated && isOnandmeHost && !isPublicPage && !isNotUseHeaderPage;

    return (
        // 테마·CssBaseline 은 index.js 루트 ThemeProvider(muiTheme)에서 한 번만 적용한다.
        <ChatbotProvider authenticated={chatbotAuthed}>
        <BrowserRouter>
            {/* 라우터 안에서만 location 을 구독할 수 있다 — App(라우터의 부모)에 경로를 되돌려준다. */}
            <LocationSync onChange={setPathname} />
            <ScrollToTop />

            {/* 단독 페이지 */}
            <Route exact path="/qr-event-participant-attendance" component={AttendancePage} />            {/* <Route exact path="/public-key-visual" component={} /> */}
            {/* 휴대폰 알림 등록 — QR 로 들어오는 폰 전용. 로그인 없이 페어링 코드로만 동작한다. */}
            <Route exact path="/m/setup" component={PhoneSetupPage} />

            {!isOnandmeHost && (
                <Switch>
                    <Route path="/bizcard=:code" component={BzCardLanding} />
                </Switch>
            )}

            {/* ================================
          ✅ 정상 렌더링 (onandme.info 전용)
      ================================ */}
            {/* Tailwind 가 없어 무시되던 "bg-gray-10 min-h-screen" 을 실제 스타일로 바꿨다.
                (bg-gray-10 은 Tailwind 에도 없는 값이라 애초에 어디서도 먹지 않았다) */}
            <div style={{ minHeight: "100vh", backgroundColor: "#f8fafc" }} translate="no">

                {/*
                  사이드바 — 단일 인스턴스만 렌더.
                  과거에 같은 컴포넌트를 두 번 렌더하면서 두 번째에 onLogout prop 이 빠져
                  그게 위에 깔려 로그아웃이 silent 하게 무시되던 버그가 있었다.
                */}
                {!isPublicPage && !isNotUseHeaderPage && (
                    <OnandmeSidebar
                        open={sidebarOpen}
                        onToggle={toggleSidebar}
                        isAuthenticated={isAuthenticated}
                        isAdmin={isAdmin}
                        onLogout={handleLogout}
                    />
                )}

                {/* 알람 종은 OnandmeSidebar 의 사용자 프로필 카드 안에서 렌더링한다. */}

                <Switch>
                    {/* ✅ CHANGED: 사이드바가 fixed 이므로 content에 left padding 필요
              - 너가 이전에 “뒷 내용이 안보여” 문제 해결했던 방식과 동일
              - sidebarOpen에 따라 paddingLeft 자동 조정
          */}
                    {/*
                      [주의] 여기엔 원래 className={isFullBleedPage ? "" : "pt-20 max-w-8xl mx-auto px-4"} 가
                      걸려 있었다. 이 프로젝트에 Tailwind 가 없어 <b>한 번도 적용된 적이 없다</b> —
                      본문 최대폭·가운데정렬·좌우여백·상단여백이 전부 없는 상태로 굳었고,
                      각 페이지가 저마다 maxWidth·padding 을 직접 잡아 그 위에 맞춰졌다.

                      그래서 지금 와서 저 의도를 되살리면(폭 제한 + 가운데 + 여백) 화면이 고쳐지는 게 아니라
                      <b>모든 페이지가 한꺼번에 밀린다</b>. 죽은 클래스만 걷어내고 현재 동작은 그대로 둔다.
                      본문 폭을 통일하려면 페이지별 여백을 함께 정리하는 별도 작업으로 다뤄야 한다.
                    */}
                    <main
                        style={{
                            paddingLeft: !isPublicPage && !isNotUseHeaderPage ? (sidebarOpen ? OPEN_W : CLOSED_W) : undefined,
                            transition: "padding-left 240ms cubic-bezier(.2,.8,.2,1)",
                            height: "100vh",
                            boxSizing: "border-box",
                            overflowY: isFullBleedPage ? "hidden" : "auto",
                        }}
                    >
                        <Route path="/onandme">
                            {/*<OnandmeHeader />*/}
                            <Switch>
                                <Route exact path="/onandme/proposal" component={ProposalMain} />
                                <Route exact path="/onandme/proposal/upload" component={ProposalUpload} />
                                <Route exact path="/onandme/upload/3d" component={SpaceContentUploadPage} />
                                <Route exact path="/onandme/list/3d" component={SpaceContentListPage} />
                                <Route exact path="/onandme/upload/2d" component={KeyVisualUploader} />
                                <Route exact path="/onandme/list/2d" component={KeyVisualListPage} />
                                <Route exact path="/onandme/event-venue" component={EventVenuePage} />
                                <Route exact path="/onandme/upload/event-venue" component={EventVenueUploadPage} />
                            </Switch>
                        </Route>

                        {/* 외부 키비주얼 열람 (로그인 불필요, 사이드바 없음) */}
                        <Route exact path="/external-keyvisual" component={ExternalKeyVisualPage} />

                        {/* 공개 페이지 */}
                        <Route exact path="/qr-event-participant-request/:eventName" component={QrEventParticipantRequestForm} />
                        <Route exact path="/qr-event-participant-check/:eventName" component={QrEventMyParticipantCheckPage} />
                        <Route exact path="/qr-event-participant-my-view" component={QrEventParticipantMyView} />

                        {/* 관리자 */}
                        <Route exact path="/qr-event-admin-list" component={QrEventAdminListPage} />

                        {/* 일반 */}
                        {/* / 는 사내 대시보드 (요약 화면). 기존의 나라장터 공고 검색은 /g2b 로 이동. */}
                        <Route exact path="/" component={DashboardPage} />
                        <Route exact path="/g2b" component={MainSearchPage} />
                        <Route path="/search/companies/" component={CompanySearchPage} />
                        <Route exact path="/employees" render={() => { window.location.replace("/hr"); return null; }} />
                        <Route exact path="/login" component={LoginPage} />
                        {/* /me 페이지는 대시보드(/) 로 흡수됨 — 호환성 위해 / 로 redirect */}
                        <Route exact path="/me" render={() => { window.location.replace("/"); return null; }} />
                        {/* 보관함 — /channel/:channelId? 보다 <b>먼저</b> 와야 한다.
                            뒤에 두면 "archived" 가 channelId 로 잡혀 채널 상세가 열린다(Switch 는 첫 매치만 쓴다). */}
                        <Route exact path="/channel/archived" component={ArchivedChannelsPage} />
                        <Route path="/channel/:channelId?" component={ChannelPage} />
                        {/* 사내 게시판 — /board, /board/{게시판}, /board/{게시판}/{글} */}
                        <Route path="/board/:boardTypeId?/:postId?" component={BoardPage} />
                        <Route path="/approval" component={ApprovalPage} />
                        <Route path="/finance" component={FinancePage} />
                        <Route path="/hr" component={HrPage} />
                        <Route path="/calendar" component={CalendarPage} />
                        <Route path="/equipment" component={EquipmentPage} />
                        <Route path="/meeting-room" component={MeetingRoomPage} />
                        <Route path="/meeting-minutes" component={MeetingMinutesPage} />
                        <Route path="/vehicle-log" component={VehicleLogPage} />
                        <Route path="/mail" component={MailPage} />
                        {/* 사내 연락처 — 메일과 별개인 독립 라우트(같은 ContactsPanel 재사용, /mail 로 이동 안 함) */}
                        <Route path="/contacts" component={ContactsPage} />
                        <Route path="/feedback" component={FeedbackPage} />
                        <Route path="/work-request" component={WorkRequestPage} />
                        <Route exact path="/admin/system-setting" component={SystemSettingPage} />
                    </main>
                </Switch>
            </div>

            {/* 로그인 직후 1회 뜨는 시스템 안내 공지 (공개 페이지·헤더 미사용 페이지 제외) */}
            {isAuthenticated && !isPublicPage && !isNotUseHeaderPage && (
                <LoginAnnouncementModal
                    open={showAnnouncement}
                    onClose={(open, reason) => {

                        if (reason !== 'backdropClick') {
                            setShowAnnouncement(false)
                        }

                    }}
                />
            )}

            {/* 챗봇 — 로그인 후 모든 페이지에서 우측 하단 노출. 영구 숨김은 Provider 가 관리. */}
            <ChatbotFAB />
            <ChatbotWindow />
            <ChatbotShowToggle />
        </BrowserRouter>
        </ChatbotProvider>
    );
}
